import useMeasure from '@/hooks/useMeasure/useMeasure';
import { animated, useSpring } from '@react-spring/web';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import styles from './AccordionItem.module.scss';
import type { AccordionItemProps } from './interfaces';

const AccordionItem = ({
  headline,
  children,
  className,
  isActive = false,
  onClick,
  color = 'default',
  hasFaqSchema = false,
}: AccordionItemProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const accordionOpen = isAccordionOpen || isActive;

  const childrenRef = useRef<HTMLDivElement>(null);

  const { height } = useMeasure(childrenRef, true);

  const springProps = useSpring({
    opacity: accordionOpen ? 1 : 0,
    maxHeight: accordionOpen ? `${height}px` : '0px',
    config: { duration: 300 },
  });

  const handleOnClick = () => {
    onClick ? onClick() : setIsAccordionOpen(!isAccordionOpen);
  };

  const questionSchemaProps = hasFaqSchema
    ? { itemScope: true, itemType: 'https://schema.org/Question' }
    : {};

  const answerSchemaProps = hasFaqSchema
    ? { itemScope: true, itemType: 'https://schema.org/Answer', itemProp: 'acceptedAnswer' }
    : {};

  return (
    <div
      className={clsx(styles.base, styles[color], { [styles.open]: accordionOpen }, className)}
      onClick={() => handleOnClick()}
      {...questionSchemaProps}
    >
      <hr className={styles.hr} />

      <div className={styles.headlineContainer}>
        <div className={styles.headline} itemProp={hasFaqSchema ? 'name' : undefined}>
          {headline}
        </div>
        <div className={styles.button} />
      </div>

      <animated.div style={{ overflow: 'hidden', ...springProps }}>
        <div className={styles.contentContainer} ref={childrenRef}>
          <div className={styles.description} {...answerSchemaProps}>
            <div itemProp={hasFaqSchema ? 'text' : undefined}>{children}</div>
          </div>
        </div>
      </animated.div>

      <hr className={styles.hr} />
    </div>
  );
};

export default AccordionItem;
